import { OnboardingStepType } from "./onboarding-step-type.enum";

/**
 * Canonical definition of the Toll Brothers co-branded onboarding partition
 * (EN-5606). This is the single source of truth for the Toll flow, shared by:
 *  - the API, which seeds one `DesignProjectOnboardingStep` row per entry at
 *    checkout (see `getTollOnboardingSteps` in design-project.service), and
 *  - the frontend, whose `TOLL_ONBOARDING_STEPS` (step order, labels, routing)
 *    derives from this list.
 *
 * `key` is the URL segment (`/toll/design-project/:id/onboarding/:key`),
 * `stepType` is the persisted `DesignProjectOnboardingStepType`, and `position`
 * is the seeded ordering. Keeping key ↔ stepType here means backend and
 * frontend can never drift.
 */
export interface TollOnboardingStepDefinition {
  key: string;
  label: string;
  stepType: OnboardingStepType;
  position: number;
}

export const TOLL_ONBOARDING_STEP_DEFINITIONS: readonly TollOnboardingStepDefinition[] =
  [
    {
      key: "property",
      label: "Property",
      stepType: OnboardingStepType.TOLL_PROPERTY,
      position: 0,
    },
    {
      key: "style-inspiration",
      label: "Style & Inspiration",
      stepType: OnboardingStepType.TOLL_STYLE_INSPIRATION,
      position: 1,
    },
    {
      key: "goals",
      label: "Goals",
      stepType: OnboardingStepType.TOLL_GOALS,
      position: 2,
    },
    {
      key: "wishlist",
      label: "Wishlist",
      stepType: OnboardingStepType.TOLL_WISHLIST,
      position: 3,
    },
    {
      key: "prioritize",
      label: "Prioritize",
      stepType: OnboardingStepType.TOLL_PRIORITIZE,
      position: 4,
    },
    {
      // The final Submit step reuses REVIEW: writing review=100 is what
      // triggers the server-side completion path (PM assignment, Firestore
      // migration, HubSpot sync), shared with normal onboarding.
      key: "submit",
      label: "Submit",
      stepType: OnboardingStepType.REVIEW,
      position: 5,
    },
  ] as const;

/** Toll step `stepType`s in flow order (used for seeding). */
export const TOLL_ONBOARDING_STEP_TYPES: readonly OnboardingStepType[] =
  TOLL_ONBOARDING_STEP_DEFINITIONS.map((step) => step.stepType);

/** Resolve a Toll step definition by its URL `key`. */
export function getTollStepByKey(
  key: string,
): TollOnboardingStepDefinition | undefined {
  return TOLL_ONBOARDING_STEP_DEFINITIONS.find((step) => step.key === key);
}
